// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button } from './Button';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { logger } from '../lib/logger';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';

export function ErrorModal() {
  const { sessionError, showSessionErrorModal, clearSession, clearSessionError } = useGameStore();
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Don't show Session Expired modal on auth pages - user is already trying to log in
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isSessionExpiredError = sessionError?.toLowerCase().includes('expired');

  // Suppress session expired errors only on auth pages
  // Do NOT suppress on /game/ or /lobby/ - these need to show errors when sessions become invalid
  const shouldSuppressSessionExpired = isSessionExpiredError && isAuthPage;

  // Clear session expired error silently when we should suppress it
  useEffect(() => {
    if (shouldSuppressSessionExpired && showSessionErrorModal) {
      logger.debug('error_modal_suppressed', {
        reason: 'on_auth_page',
        path: location.pathname,
      });
      clearSessionError();
    }
  }, [
    shouldSuppressSessionExpired,
    showSessionErrorModal,
    clearSessionError,
    isAuthPage,
    location.pathname,
  ]);

  const trapRef = useFocusTrap(true);

  if (!showSessionErrorModal || !sessionError || shouldSuppressSessionExpired) {
    return null;
  }

  const handleReturnToLobby = () => {
    clearSession();
    clearSessionError();
    navigate('/', { replace: true });
  };

  // Determine error type for appropriate icon and styling
  const isAuthError =
    sessionError.toLowerCase().includes('expired') ||
    sessionError.toLowerCase().includes('session expired');
  const isTerminated = sessionError.includes('terminated') || sessionError.includes('cleared');
  const isSessionNotFound =
    sessionError.includes('Session not found') || sessionError.includes('not found');

  const getIcon = () => {
    if (isAuthError) return '🔐';
    if (isTerminated) return '⚠️';
    if (isSessionNotFound) return '❌';
    return '⚠️';
  };

  const getTitle = () => {
    if (isAuthError) return 'Session Expired';
    if (isTerminated) return 'Room Closed';
    if (isSessionNotFound) return 'Room Not Found';
    return 'Session Ended';
  };

  return (
    <div
      ref={trapRef}
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      data-testid="error-modal-overlay"
    >
      {/* Themed Modal */}
      <div
        className="rounded-neo-lg p-8 max-w-md w-full relative animate-in fade-in zoom-in duration-300"
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '6px solid var(--color-text-primary)',
          boxShadow: '16px 16px 0px rgba(0, 0, 0, 0.3)',
        }}
        data-testid="error-modal-content"
      >
        <div className="text-center">
          {/* Icon */}
          <div className="text-7xl mb-6 animate-bounce">{getIcon()}</div>

          {/* Title */}
          <h2
            className="font-display text-3xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
            data-testid="error-modal-title"
          >
            {getTitle()}
          </h2>

          {/* Message */}
          <div
            className="rounded-lg p-4 mb-6"
            style={{
              backgroundColor: 'var(--color-surface-hover)',
              border: '3px solid var(--color-accent)',
            }}
          >
            <p
              className="text-base leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
              data-testid="error-modal-message"
            >
              {sessionError}
            </p>
          </div>

          {/* Action Button */}
          <Button
            variant="primary"
            onClick={handleReturnToLobby}
            className="w-full text-lg py-3"
            data-testid="error-modal-return-button"
          >
            {isAuthError && isAuthenticated() ? '🏠 Return to Login' : '🏠 Return to Home'}
          </Button>

          {/* Additional context for auth errors */}
          {isAuthError && (
            <p className="text-xs mt-4 italic" style={{ color: 'var(--color-text-secondary)' }}>
              Your session has expired. Please log in again to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
